import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, MessageSquare, Calendar, ChevronRight, ArrowLeft, Mic } from 'lucide-react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useAdmin } from '@/hooks/useAdmin'

interface Conversation {
    id: string
    title: string | null
    created_at: string
    is_voice_enabled: boolean
    message_count?: number
}

interface Message {
    id: string
    role: 'user' | 'assistant'
    content: string
    created_at: string
}

interface Props {
    residentId: string
    residentName: string
    onClose: () => void
}

const T = {
    sage: '#4a7c5e',
    sageLight: '#e8f2ec',
    navy: '#1a2740',
    cream: '#faf7f2',
    creamDark: '#f0ebe0',
    white: '#ffffff',
    slate: '#4a5568',
    muted: '#8a94a6',
    fontDisplay: "'Playfair Display', Georgia, serif",
    fontBody: "'Source Serif 4', Georgia, serif",
}

export function MemberConversationViewer({ residentId, residentName, onClose }: Props) {
    const supabase = createClientComponentClient()
    const { isSuperAdmin } = useAdmin()
    const [conversations, setConversations] = useState<Conversation[]>([])
    const [selectedConversation, setSelectedConversation] = useState<string | null>(null)
    const [messages, setMessages] = useState<Message[]>([])
    const [loading, setLoading] = useState(true)
    const [messagesLoading, setMessagesLoading] = useState(false)
    const [accessDenied, setAccessDenied] = useState<string | null>(null)

    useEffect(() => {
        checkAccessAndFetch()
    }, [residentId])

    const checkAccessAndFetch = async () => {
        setLoading(true)
        try {
            // 1. Fetch user data and facility/hub info
            const { data: targetUser, error: userError } = await supabase
                .from('users')
                .select('preferences, facility_id')
                .eq('id', residentId)
                .single()

            if (userError || !targetUser.facility_id) {
                setAccessDenied("User profile is not associated with a facility or hub.")
                setLoading(false)
                return
            }

            // 2. Fetch hub type
            const { data: hubData, error: hubError } = await supabase
                .from('facilities')
                .select('type')
                .eq('id', targetUser.facility_id)
                .single()

            if (hubError) throw hubError

            // 3. Apply rules
            // Super Admin always allowed
            if (isSuperAdmin) {
                return fetchConversations()
            }

            // Facilities are NEVER allowed to view conversations
            if (hubData.type === 'facility') {
                setAccessDenied("Institutional Privacy: Facility administrators are not permitted to view individual conversation transcripts to protect resident privacy.")
                setLoading(false)
                return
            }

            // Family Hubs allowed IF not disabled by preference
            const preferences = (targetUser.preferences as any) || {}
            const isVisible = preferences.allow_family_admin_view !== false
            if (!isVisible) {
                setAccessDenied("Privacy Shield: This family member has disabled conversation sharing for their profile.")
                setLoading(false)
                return
            }

            // Otherwise fetch
            await fetchConversations()
        } catch (err) {
            console.error('Error checking access:', err)
            setAccessDenied("Failed to verify access permissions.")
        } finally {
            setLoading(false)
        }
    }

    const fetchConversations = async () => {
        try {
            const { data, error } = await supabase
                .from('conversations')
                .select(`
                    id, 
                    title, 
                    created_at, 
                    is_voice_enabled,
                    messages:messages(count)
                `)
                .eq('user_id', residentId)
                .order('created_at', { ascending: false })

            if (error) throw error

            const transformedData = data.map((c: any) => ({
                ...c,
                message_count: c.messages?.[0]?.count || 0
            }))

            setConversations(transformedData)
        } catch (err) {
            console.error('Error fetching conversations:', err)
        }
    }

    const fetchMessages = async (convId: string) => {
        setMessagesLoading(true)
        setSelectedConversation(convId)
        try {
            const { data, error } = await supabase
                .from('messages')
                .select('*')
                .eq('conversation_id', convId)
                .order('created_at', { ascending: true })

            if (error) throw error
            setMessages(data as Message[])
        } catch (err) {
            console.error('Error fetching messages:', err)
        } finally {
            setMessagesLoading(false)
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-10"
            style={{ background: 'rgba(26,39,64,0.6)', backdropFilter: 'blur(8px)' }}
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 20, opacity: 0 }}
                style={{
                    background: T.cream,
                    borderRadius: '24px',
                    width: '100%',
                    maxWidth: '900px',
                    height: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    border: `1px solid ${T.creamDark}`
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    padding: '24px 32px',
                    background: T.white,
                    borderBottom: `1px solid ${T.creamDark}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {(selectedConversation || accessDenied) && (
                            <button
                                onClick={() => { setSelectedConversation(null); if (accessDenied) onClose(); }}
                                style={{
                                    width: '36px', height: '36px', borderRadius: '50%',
                                    background: T.cream, border: 'none', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                    color: T.navy
                                }}
                            >
                                <ArrowLeft size={18} />
                            </button>
                        )}
                        <div>
                            <h2 style={{ fontFamily: T.fontDisplay, fontSize: '22px', fontWeight: 700, color: T.navy, margin: 0 }}>
                                {accessDenied ? 'Privacy Shield' : selectedConversation ? 'Chat Transcript' : `${residentName}'s Conversations`}
                            </h2>
                            <p style={{ fontSize: '13px', color: T.muted, margin: 0 }}>
                                {accessDenied ? 'Access Restricted' : selectedConversation ? 'Care Coordination View' : 'Timeline of engagement'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            background: T.cream, border: 'none', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            color: T.muted
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto" style={{ padding: '32px' }}>
                    {loading ? (
                        <div className="flex h-full items-center justify-center">
                            <LoadingSpinner />
                        </div>
                    ) : accessDenied ? (
                        <div style={{ textAlign: 'center', padding: '60px 40px', background: T.white, borderRadius: '24px', border: `1px solid ${T.creamDark}` }}>
                            <div style={{ width: '64px', height: '64px', background: '#fef2f2', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                                <X size={32} color="#dc2626" />
                            </div>
                            <h3 style={{ fontFamily: T.fontDisplay, fontSize: '24px', fontWeight: 700, color: T.navy, marginBottom: '16px' }}>Visibility Restricted</h3>
                            <p style={{ color: T.slate, fontSize: '16px', lineHeight: 1.6, maxWidth: '500px', margin: '0 auto' }}>{accessDenied}</p>
                            <button
                                onClick={onClose}
                                style={{ marginTop: '32px', padding: '12px 32px', borderRadius: '14px', background: T.navy, color: T.white, border: 'none', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody }}
                            >
                                Go Back
                            </button>
                        </div>
                    ) : selectedConversation ? (
                        /* Transcript View */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '700px', margin: '0 auto' }}>
                            {messagesLoading ? (
                                <LoadingSpinner />
                            ) : messages.length === 0 ? (
                                <p style={{ textAlign: 'center', color: T.muted }}>No messages in this conversation.</p>
                            ) : (
                                messages.map((msg, i) => (
                                    <div
                                        key={msg.id}
                                        style={{
                                            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                            maxWidth: '85%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                                        }}
                                    >
                                        <span style={{ fontSize: '10px', fontWeight: 800, color: T.muted, textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.05em' }}>
                                            {msg.role === 'user' ? residentName : 'Sage'}
                                        </span>
                                        <div style={{
                                            padding: '16px 20px',
                                            borderRadius: msg.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                                            background: msg.role === 'user' ? T.navy : T.white,
                                            color: msg.role === 'user' ? T.white : T.navy,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                            border: msg.role === 'user' ? 'none' : `1px solid ${T.creamDark}`,
                                            fontSize: '15px',
                                            lineHeight: 1.6,
                                            fontFamily: T.fontBody
                                        }}>
                                            {msg.content}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        /* Conversations List */
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                            {conversations.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '60px 20px', background: T.white, borderRadius: '20px', border: `1px dashed ${T.muted}` }}>
                                    <MessageSquare size={48} color={T.muted} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                                    <h3 style={{ fontWeight: 700, color: T.navy, fontSize: '18px' }}>No activity yet</h3>
                                    <p style={{ color: T.muted }}>{residentName} hasn't started any conversations with Sage.</p>
                                </div>
                            ) : (
                                conversations.map((conv) => (
                                    <button
                                        key={conv.id}
                                        onClick={() => fetchMessages(conv.id)}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '20px 24px',
                                            background: T.white,
                                            border: `1px solid ${T.creamDark}`,
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                        }}
                                        className="hover:!border-emerald-500 hover:!shadow-md group"
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                            <div style={{
                                                width: '48px', height: '48px', borderRadius: '12px',
                                                background: conv.is_voice_enabled ? T.sageLight : T.cream,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: conv.is_voice_enabled ? T.sage : T.muted
                                            }}>
                                                {conv.is_voice_enabled ? <Mic size={20} /> : <MessageSquare size={20} />}
                                            </div>
                                            <div>
                                                <h3 style={{ fontWeight: 700, color: T.navy, margin: 0, fontSize: '16px' }}>
                                                    {conv.title || 'Ongoing Conversation'}
                                                </h3>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
                                                    <span style={{ fontSize: '12px', color: T.muted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Calendar size={12} /> {new Date(conv.created_at).toLocaleDateString()}
                                                    </span>
                                                    <span style={{ fontSize: '12px', color: T.muted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <MessageSquare size={12} /> {conv.message_count} messages
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronRight size={20} className="text-slate-300 group-hover:text-emerald-500 transform group-hover:translate-x-1 transition-all" />
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Footer disclaimer */}
                <div style={{
                    padding: '16px 32px',
                    background: T.white,
                    borderTop: `1px solid ${T.creamDark}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: T.sage }} />
                    <p style={{ fontSize: '11px', color: T.muted, margin: 0, fontWeight: 500 }}>
                        {isSuperAdmin ? 'Super Admin Override: Full access enabled.' : 'Family Hub Transparency: Accessible for care coordination when enabled by member.'}
                    </p>
                </div>
            </motion.div>
        </motion.div>
    )
}
